import { useState, useEffect, useCallback, useMemo } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors, getAccountChartColor } from "../../lib/colors"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import { readStoredPeriods } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api, type ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanLockNotice from "../../components/account/PlanLockNotice"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, todayStr } from "../../lib/csv"
import type { FanUser } from "../../types/analytics"
import "../../styles/analytics.scss"

const STORAGE_KEY = "lyto.fanUsersPeriod"

const FanUsersPage = () => {
  const { selectedAccountIds, organizations, setSingleAccountOnly } = useSelectedAccount()
  const navigate = useNavigate()

  const accountColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    selectedAccountIds.forEach((id, i) => {
      const account = organizations.flatMap(o => o.accounts).find(a => a.id === id)
      if (account) map[account.username] = getAccountChartColor(i).border
    })
    return map
  }, [selectedAccountIds, organizations])

  const selectedAccounts = useMemo(() =>
    organizations.flatMap(o => o.accounts).filter(a => selectedAccountIds.includes(a.id)),
    [selectedAccountIds, organizations]
  )
  const locked = selectedAccounts.length > 0 && selectedAccounts.some(a => !a.features.fan_user)

  const [fans, setFans]       = useState<FanUser[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState<string | null>(null)
  const [periods, setPeriods] = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))

  useEffect(() => {
    setSingleAccountOnly(periods.length > 1)
  }, [periods, setSingleAccountOnly])

  const fetchData = useCallback(async () => {
    if (selectedAccountIds.length === 0 || locked) { setLoading(false); return }
    setLoading(true)
    setForbidden(null)
    try {
      const p = periods[0]
      const params = new URLSearchParams({
        start_date: p.startDate,
        end_date:   p.endDate,
      })
      selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
      const res = await api.get<FanUser[]>(`/analytics/fan_users?${params}`)
      setFans(res)
    } catch (e) {
      const err = e as ApiError
      if (err.status === 403) setForbidden(err.data?.error ?? `${PAGE_TITLES["/fan-users"]}はプロプラン以上でご利用いただけます`)
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, periods, locked])

  useEffect(() => { fetchData() }, [fetchData])

  const groupedByAccount = useMemo(() => {
    const map = new Map<string, FanUser[]>()
    fans.forEach(f => {
      const key = f.account_username
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(f)
    })
    return map
  }, [fans])

  const renderTable = (fanList: FanUser[]) => {
    const handleCsvDownload = () => downloadCsv(
      [
        ["表示名", "投稿数", "いいね合計", "平均いいね", "平均コメント", "最終タグ日"],
        ...fanList.map(f => [
          f.display_name, f.posts_count, f.likes_count,
          f.avg_likes, f.avg_comments, f.last_tagged_on ?? "",
        ]),
      ],
      `fan_users_${todayStr()}.csv`
    )
    return (
    <div className="posts-table">
      <div className="posts-table__header">
        <span className="posts-table__title">ファンユーザー一覧</span>
        <button className="summary-table__csv-btn" onClick={handleCsvDownload}>↓ CSV</button>
      </div>
      <div className="posts-table__wrap">
        <table>
          <thead>
            <tr>
              <th>表示名</th>
              <th>投稿数</th>
              <th>いいね合計</th>
              <th>平均いいね</th>
              <th>平均コメント</th>
              <th>最終タグ日</th>
            </tr>
          </thead>
          <tbody>
            {fanList.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ color: colors.gray400, textAlign: "center", padding: "32px 0" }}>
                  ファンユーザーがいません
                </td>
              </tr>
            ) : (
              fanList.map(fan => (
                <tr
                  key={`${fan.instagram_account_id}-${fan.owner_ig_id}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/fan-users/${fan.owner_ig_id}?account_id=${fan.instagram_account_id}`)}
                >
                  <td>{fan.display_name}</td>
                  <td>{fan.posts_count.toLocaleString()}</td>
                  <td>{fan.likes_count.toLocaleString()}</td>
                  <td>{fan.avg_likes != null ? fan.avg_likes.toLocaleString() : "—"}</td>
                  <td>{fan.avg_comments != null ? fan.avg_comments.toLocaleString() : "—"}</td>
                  <td>{fan.last_tagged_on ?? "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
  }

  return (
    <AppLayout pageTitle={PAGE_TITLES["/fan-users"]}>
      <div className="analytics">
        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : locked ? (
          <PlanLockNotice message={`${PAGE_TITLES["/fan-users"]}はプロプラン以上でご利用いただけます`} />
        ) : forbidden ? (
          <PlanLockNotice message={forbidden} />
        ) : (
          <>
            <div className="analytics__header">
              <PeriodSelectorModal
                periods={periods}
                onChange={setPeriods}
                storageKey={STORAGE_KEY}
              />
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : selectedAccountIds.length === 1 ? (
              <div className="analytics__section">
                {renderTable(fans)}
              </div>
            ) : (
              Array.from(groupedByAccount.entries()).map(([accountUsername, accountFans]) => (
                <div key={accountUsername} className="analytics__section">
                  <div className="analytics__section-title">
                    <span
                      className="posts-table__username"
                      style={{ background: accountColorMap[accountUsername] ?? colors.gray400 }}
                    >
                      @{accountUsername}
                    </span>
                  </div>
                  {renderTable(accountFans)}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default FanUsersPage
